import type { ScenarioSpec } from "./types";
import { officeUnderAttackScenario as OUA } from "./office-under-attack.scenario";

/**
 * Reception-terminal briefing on passwords and sign-in.
 * The film is on the left. The practice is Inside the Bank, opened with
 * skipQuiz so its own quiz is replaced by a return to this terminal.
 * The quiz lives only on this hub. Not on the public library until published.
 */
const VIDEO = "/cdn/sim/awareness-videos/passwords-mfa/passwords-mfa.mp4";
const FILM_STILL = "/cdn/sim/awareness-videos/passwords-mfa/wide/19-caller.jpg";
const ATTACK_STILL = "/cdn/sim/awareness-videos/passwords-mfa/wide/16-fake-page.jpg";

export const passwordsMfaScenario: ScenarioSpec = {
  version: 1,
  id: "passwords-mfa",
  title: "Passwords and sign-in",
  summary: {
    goal: "See how a reused password pattern, public personal facts and a fake sign-in page let an attacker in, and why each account needs its own password.",
    youDo: "Watch a film, then play the attacker in Inside the Bank: try a leaked password, send a fake salary email, collect passwords and use a guessing tool, then answer five questions.",
    duration: "Core 4-7 min, Inside the Bank adds about 15 min",
    tags: ["passwords", "leaked_credentials", "fake_login_pages", "oversharing", "mfa"],
    audience: ["all_staff"],
    needs: ["sound"],
  },
  topic: "A pattern, a personal fact, or a login page you were sent can be enough to get in",
  language: "en",
  passThreshold: 60,
  allowFreeExplore: false,
  voiceAttackers: OUA.voiceAttackers,
  scene: OUA.scene,

  devices: [
    {
      id: "password_terminal",
      kind: "breachbrief",
      config: {
        noticeTag: "PASSWORDS",
        noticeKicker: "SECURITY AWARENESS",
        title: "Passwords and sign-in",
        offLabel: "Switch on the welcome terminal",
        voiceId: "onyx",
        progressKey: "passwords-mfa",
        idleImage: FILM_STILL,
        videoFirst: true,
        lines: [
          "A password that follows a pattern, or one built from something public, is easy for someone else to try.",
          "Watch the film on the left, then play the attack. The quiz stays on this terminal until you are ready.",
        ],
        hubIntro: "Watch the film, then see how a leaked password gets into a bank.",
        hubVideo: {
          title: "Passwords and MFA",
          blurb: "What makes a password easy to guess, and what a second check changes.",
          src: VIDEO,
          poster: FILM_STILL,
        },
        exercisesKicker: "1 interactive exercise",
        exercisesLabel: "Practise",
        exercises: [
          {
            id: "bank-attack",
            label: "Play the attack",
            blurb: "A leaked streaming password, a salary email, and a public Facebook page. See how far that gets into the bank.",
            key: "game",
            completionBeatId: "b_bank_return",
            icon: "chapters",
            imageUrl: ATTACK_STILL,
            actionLabel: "Play",
            doneLabel: "Done",
            href: "/?scenario=inside-the-bank&beat=b_to_monitor&return=passwords-mfa&skipQuiz=1",
          },
        ],
        cardsTitle: "Practise",
        cardsIntro: "",
        cards: [],
        finishLabel: "Finish the briefing",
        finishHint: "Continue to the quiz",
        finishEarlyHint: "You can take the quiz now, or watch the film and play the attack first.",
      },
    },
  ],

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Passwords and sign-in",
      kicker: "Reception",
      guide: {
        title: "Start",
        body: "You start at an unattended reception. The welcome terminal shows a film on passwords and sign-in and lets you switch sides: you play an attacker looking at one employee of the fictional Aster Bank, starting from a streaming password that has just leaked.",
      },
    },
    { id: "b_cam_terminal", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_hub",
      type: "device",
      deviceId: "password_terminal",
      guide: {
        title: "Reception terminal",
        body: "Switch on the welcome terminal. The film is on the left. The attack is beside it. The quiz is the button below.",
      },
      completeOn: { action: "continue" },
    },
    {
      id: "b_quiz",
      type: "quiz",
      title: "Check what you would do",
      quiz: [
        {
          id: "q_pattern",
          title: "Question 1",
          prompt: "Your old password for a shopping site was Summer2025!. For work you now use Summer2026!. Why is that risky?",
          options: [
            { id: "a", label: "It is not. Changing the year makes it a brand-new secret.", icon: "x" },
            { id: "b", label: "If the old one leaks, an attacker will try the same pattern with a newer year.", correct: true, icon: "check" },
            { id: "c", label: "It is only risky if both sites belong to the same company.", icon: "alert" },
          ],
          rationale: "Editing one familiar password leaves a predictable pattern. Each account needs its own password, made from scratch.",
        },
        {
          id: "q_click",
          title: "Question 2",
          prompt: "An email links to a sign-in page that looks like your company's. Which step hands over your password?",
          options: [
            { id: "a", label: "Opening and reading the email.", icon: "x" },
            { id: "b", label: "Clicking the link and then closing the page.", icon: "x" },
            { id: "c", label: "Typing your username and password into that page.", correct: true, icon: "check" },
          ],
          rationale: "The password is lost when it is typed into the attacker's page. Open the real service from a bookmark or the app instead, and report the email.",
        },
        {
          id: "q_strength",
          title: "Question 3",
          prompt: "Your password is long and random. When does that not protect you?",
          options: [
            { id: "a", label: "When someone tries to guess it.", icon: "x" },
            { id: "b", label: "When you type it into a fake sign-in page.", correct: true, icon: "check" },
            { id: "c", label: "Never. A strong password is always safe.", icon: "alert" },
          ],
          rationale: "Strength stops guessing. It does not stop a fake login page. An exposed password is unsafe no matter how strong.",
        },
        {
          id: "q_clues",
          title: "Question 4",
          prompt: "Your dog Biscuit and your birth year are on your public social media. Which password is easiest to work out?",
          options: [
            { id: "a", label: "Four random, unrelated words.", icon: "x" },
            { id: "b", label: "Biscuit2004, your dog's name and birth year.", correct: true, icon: "check" },
            { id: "c", label: "A long password generated by a password manager.", icon: "alert" },
          ],
          rationale: "A pet name and a birth year are public building blocks a guessing tool can combine. Random words give it nothing to assemble.",
        },
        {
          id: "q_mfa",
          title: "Question 5",
          prompt: "Your work password has leaked, but the account also asks for a second check (MFA). What does that change?",
          options: [
            { id: "a", label: "Nothing. The attacker is in as soon as they have the password.", icon: "x" },
            { id: "b", label: "The password alone is not enough, as long as you never approve or read out a code you did not ask for.", correct: true, icon: "check" },
            { id: "c", label: "The leak no longer matters, so the password can stay as it is.", icon: "alert" },
          ],
          rationale: "A second check stops a leaked password on its own. Still change the password, and treat any sign-in prompt you did not start as an attack.",
        },
      ],
    },
  ],

  edges: [
    { from: "b_intro", to: "b_cam_terminal" },
    { from: "b_cam_terminal", to: "b_hub" },
    { from: "b_hub", to: "b_quiz", on: "finish" },
  ],

  initialBeatId: "b_intro",
};
